import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Badge, Card, CardBody, Progress } from 'reactstrap';
import classNames from 'classnames';

import { Button, Icon } from 'js/components';
import { faChevronDown, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  faFlask,
  faUserPlus,
  faTableLayout,
} from '@fortawesome/pro-regular-svg-icons';

import { useOnboarding } from './OnboardingContext';
import { buildSuggestions, countFinished } from './steps';
import { CARD_ID } from './constants';
import { StepMarker, StepTitle } from './StepParts';
import ProgressRing from './ProgressRing';
import styles from './Onboarding.module.scss';

// The small uppercase label above the title and over the suggestions.
const LABEL = 'small text-uppercase font-weight-bold';

const SUGGESTION_ICONS = {
  sample: faFlask,
  'invite-colleague': faUserPlus,
  rubrics: faTableLayout,
};

function StepRow({ step, isOpen, onToggle, onSkip, onUnskip }) {
  const bodyId = `getting-started-step-${step.key}`;

  return (
    <li className={styles.step}>
      <button
        type="button"
        className={styles['step-head']}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <StepMarker step={step} />
        <StepTitle step={step} className={styles.fill} />
        {step.isNext && <Badge color="success">Up next</Badge>}
        {step.status === 'skipped' && (
          <Badge className={styles['badge-skipped']}>Skipped</Badge>
        )}
        <Icon
          icon={faChevronDown}
          className={classNames(styles.chevron, {
            [styles['chevron-open']]: isOpen,
          })}
        />
      </button>
      {isOpen && (
        <div id={bodyId} className={styles['step-body']}>
          <p className={styles.prose}>{step.description}</p>
          <div className={styles.actions}>
            {step.status !== 'done' && step.cta && (
              <Button
                tag={Link}
                to={step.cta.to}
                color="primary"
                className="m-0"
              >
                {step.cta.label}
              </Button>
            )}
            {step.status === 'todo' && (
              <Button color="link" className="m-0" onClick={onSkip}>
                Skip this step
              </Button>
            )}
            {step.status === 'skipped' && (
              <Button color="link" className="m-0" onClick={onUnskip}>
                Undo skip
              </Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function Suggestions({ progress, workspaceId, createSampleTeam }) {
  const history = useHistory();
  const [creating, setCreating] = useState(false);

  const handleCreateSample = async () => {
    setCreating(true);
    const updated = await createSampleTeam();
    setCreating(false);

    if (updated?.sample_team) {
      history.push(`/w/${workspaceId}/teams/${updated.sample_team.id}`);
    }
  };

  return (
    <aside
      className={classNames(styles.suggestions, 'border-left')}
      aria-labelledby="getting-started-more"
    >
      <h3 id="getting-started-more" className={`${LABEL} text-muted mb-1`}>
        When you're ready
      </h3>
      {buildSuggestions(progress, workspaceId).map((suggestion) => {
        const content = (
          <>
            <Icon
              icon={SUGGESTION_ICONS[suggestion.key]}
              fixedWidth
              className={styles['suggestion-icon']}
            />
            <span>
              <span
                className={classNames(
                  'd-block font-weight-bold',
                  styles['suggestion-title']
                )}
              >
                {suggestion.title}
              </span>
              <span className="d-block small text-muted">
                {suggestion.description}
              </span>
            </span>
          </>
        );

        return suggestion.createsSample ? (
          <button
            key={suggestion.key}
            type="button"
            className={styles.suggestion}
            onClick={handleCreateSample}
            disabled={creating}
          >
            {content}
          </button>
        ) : (
          <Link
            key={suggestion.key}
            to={suggestion.to}
            className={styles.suggestion}
          >
            {content}
          </Link>
        );
      })}
    </aside>
  );
}

function CompactCard({ steps, finished, onExpand, onDismiss }) {
  const next = steps.find((step) => step.isNext);

  return (
    <Card id={CARD_ID} className={styles.card}>
      <CardBody className={styles.compact}>
        <ProgressRing done={finished} total={steps.length} size={40} />
        <div className={styles['compact-text']}>
          <strong>
            Getting started: {finished} of {steps.length} done
          </strong>
          {next && <span className="text-muted">Next: {next.title}</span>}
        </div>
        {next?.cta && (
          <Button tag={Link} to={next.cta.to} color="primary" className="m-0">
            Continue
          </Button>
        )}
        <Button color="link" className="m-0" onClick={onExpand}>
          Show all steps
        </Button>
        <Button color="link" className="m-0" onClick={onDismiss}>
          Dismiss
        </Button>
      </CardBody>
    </Card>
  );
}

function CompleteCard({ progress, workspaceId, onDismiss }) {
  const base = `/w/${workspaceId}`;

  return (
    <Card id={CARD_ID} className={styles.card}>
      <CardBody className="media alert-dismissible position-relative">
        <button
          type="button"
          className="close"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <Icon
          icon={faCircleCheck}
          size="4x"
          className="mr-3 text-success"
          fixedWidth
        />
        <div className="media-body">
          <h2 className="h4 mb-2 mt-0">You're all set</h2>
          <p>
            Run another assessment with{' '}
            {progress.latest_results?.team_name ?? 'your team'} when you're
            ready to see how the team is changing. Quick health checks work well
            quarterly, and longer assessments less often.
          </p>
          <div className={styles.actions}>
            <Button
              tag={Link}
              to={`${base}/teams/add`}
              color="primary"
              className="m-0"
            >
              Add another team
            </Button>
            <Button
              tag={Link}
              to={`${base}/users/invite`}
              color="link"
              className="m-0"
            >
              Invite a colleague
            </Button>
            <Button color="link" className="m-0" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The getting-started checklist at the top of the dashboard.
 */
export default function GettingStartedCard() {
  const onboarding = useOnboarding();
  const [openKey, setOpenKey] = useState(null);

  if (!onboarding?.progress) {
    return null;
  }

  const {
    progress,
    steps,
    workspaceId,
    collapse,
    expand,
    dismiss,
    skip,
    unskip,
    createSampleTeam,
  } = onboarding;
  const finished = countFinished(progress);

  if (progress.complete) {
    return (
      <CompleteCard
        progress={progress}
        workspaceId={workspaceId}
        onDismiss={dismiss}
      />
    );
  }

  if (progress.collapsed) {
    return (
      <CompactCard
        steps={steps}
        finished={finished}
        onExpand={expand}
        onDismiss={dismiss}
      />
    );
  }

  // The next step starts open; after that the user decides.
  const currentOpenKey = openKey ?? progress.next_step;

  return (
    <Card id={CARD_ID} className={styles.card}>
      <section aria-labelledby="getting-started-title">
        <header className={classNames(styles.header, 'border-bottom')}>
          <div className={styles['header-text']}>
            <div className={`${LABEL} text-info`}>Getting started</div>
            <h2 id="getting-started-title" className="h4 my-1">
              Run your first team assessment
            </h2>
            <p className="text-muted mb-0">
              Work through these in any order. Each one checks off when you do
              it.
            </p>
          </div>
          <div className={styles['header-progress']}>
            <div className="d-flex justify-content-between align-items-baseline">
              <strong>
                {finished} of {steps.length} done
              </strong>
              <Button
                color="link"
                size="sm"
                className="m-0 p-0"
                onClick={collapse}
              >
                Hide checklist
              </Button>
            </div>
            <Progress
              color="info"
              value={finished}
              max={steps.length}
              aria-label="Checklist progress"
            />
          </div>
        </header>
        <div className="d-flex flex-wrap">
          <ol className={classNames(styles.steps, 'list-unstyled mb-0')}>
            {steps.map((step) => (
              <StepRow
                key={step.key}
                step={step}
                isOpen={currentOpenKey === step.key}
                onToggle={() =>
                  setOpenKey(currentOpenKey === step.key ? '' : step.key)
                }
                onSkip={() => skip(step.key)}
                onUnskip={() => unskip(step.key)}
              />
            ))}
          </ol>
          <Suggestions
            progress={progress}
            workspaceId={workspaceId}
            createSampleTeam={createSampleTeam}
          />
        </div>
      </section>
    </Card>
  );
}
